import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { inventoryApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  Package, Plus, Trash2, Pencil, Check, X, Search, Minus,
} from "lucide-react";
import { cn } from "../lib/utils";

const Inventory = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Add form
  const [addName, setAddName] = useState("");
  const [addQty, setAddQty] = useState("");
  const [addUnit, setAddUnit] = useState("st");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const suggestRef = useRef(null);
  const inputRef = useRef(null);

  // Edit
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", quantity: "", unit: "" });

  useEffect(() => { loadItems(); }, []);

  const loadItems = async () => {
    try {
      const res = await inventoryApi.getAll();
      setItems(res.data);
    } catch {
      toast.error("Kunde inte ladda inventarie");
    } finally {
      setLoading(false);
    }
  };

  // Autocomplete suggestions
  const fetchSuggestions = useCallback(async (q) => {
    if (q.length < 1) { setSuggestions([]); setShowSuggestions(false); return; }
    try {
      const res = await inventoryApi.suggest(q);
      setSuggestions(res.data);
      if (res.data.length > 0 && !selectedSuggestion) setShowSuggestions(true);
    } catch {
      setSuggestions([]);
    }
  }, [selectedSuggestion]);

  useEffect(() => {
    if (selectedSuggestion) return; // Don't fetch after selecting
    const timer = setTimeout(() => fetchSuggestions(addName), 200);
    return () => clearTimeout(timer);
  }, [addName, fetchSuggestions, selectedSuggestion]);

  // Close suggestions on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (suggestRef.current && !suggestRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleSelectSuggestion = (item) => {
    setAddName(item.name);
    setAddUnit(item.unit || "st");
    setSelectedSuggestion(item);
    setShowSuggestions(false);
    setAddQty("");
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addName.trim()) return;
    const qty = parseFloat(addQty) || 0;
    if (qty <= 0) { toast.error("Ange antal"); return; }

    try {
      await inventoryApi.add({
        name: addName.trim(),
        quantity: qty,
        unit: addUnit || "st",
      });

      if (selectedSuggestion) {
        toast.success(`+${qty} ${addUnit} tillagt till "${addName}"`);
      } else {
        toast.success(`"${addName}" tillagd (${qty} ${addUnit})`);
      }

      setAddName("");
      setAddQty("");
      setAddUnit("st");
      setSelectedSuggestion(null);
      loadItems();
      inputRef.current?.focus();
    } catch {
      toast.error("Kunde inte lägga till");
    }
  };

  const handleDelete = async (id) => {
    try {
      await inventoryApi.delete(id);
      setItems(prev => prev.filter(i => i.id !== id));
      toast.success("Borttagen");
    } catch {
      toast.error("Kunde inte ta bort");
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditForm({ name: item.name, quantity: item.quantity.toString(), unit: item.unit || "st" });
  };

  const handleEditSave = async () => {
    if (!editForm.name.trim()) return;
    try {
      await inventoryApi.update(editingId, {
        name: editForm.name.trim(),
        quantity: parseFloat(editForm.quantity) || 0,
        unit: editForm.unit,
      });
      setEditingId(null);
      loadItems();
    } catch {
      toast.error("Kunde inte uppdatera");
    }
  };

  const handleQuickAdjust = async (item, delta) => {
    const newQty = Math.max(0, item.quantity + delta);
    try {
      await inventoryApi.update(item.id, { quantity: newQty });
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, quantity: newQty } : i));
    } catch {
      toast.error("Kunde inte uppdatera");
    }
  };

  // Filter items by search
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items;
    const q = searchQuery.toLowerCase();
    return items.filter(i => i.name.toLowerCase().includes(q) || (i.category || "").toLowerCase().includes(q));
  }, [items, searchQuery]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6dbf4a]"></div></div>;
  }

  return (
    <div className="space-y-5 slide-up" data-testid="inventory-page">
      {/* Header + Stats */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Package className="w-5 h-5 text-[#6dbf4a]" />
            <h1 className="text-2xl font-bold text-white tracking-tight">Förråd</h1>
          </div>
          <p className="text-white/25 text-sm">Inventarie och material i förrådet</p>
        </div>
        <span className="text-sm text-white/40">{filteredItems.length} artiklar</span>
      </div>

      {/* Add item with autocomplete */}
      <div ref={suggestRef}>
        <form onSubmit={handleAdd} className="flex gap-2 items-center flex-wrap" data-testid="add-inventory-form">
          <div className="flex-1 min-w-[150px]">
            <Input
              ref={inputRef}
              value={addName}
              onChange={(e) => { setAddName(e.target.value); setSelectedSuggestion(null); setShowSuggestions(true); }}
              onFocus={() => { if (suggestions.length > 0 && !selectedSuggestion) setShowSuggestions(true); }}
              placeholder="Artikelnamn..."
              autoComplete="off"
              className="bg-[#0d1218] border-white/[0.06] text-white"
              data-testid="add-name"
            />
          </div>

          <Input value={addQty} onChange={(e) => setAddQty(e.target.value)}
            placeholder="Antal" type="number" step="any" min="0"
            className="bg-[#0d1218] border-white/[0.06] text-white w-20 text-center"
            data-testid="add-qty" />
          <Input value={addUnit} onChange={(e) => setAddUnit(e.target.value)}
            placeholder="Enhet"
            className="bg-[#0d1218] border-white/[0.06] text-white w-16 text-center"
            data-testid="add-unit" />
          <Button type="submit" disabled={!addName.trim() || !addQty}
            className="bg-[#6dbf4a] hover:bg-[#5da83d] text-white gap-1.5"
            data-testid="add-btn">
            <Plus className="w-4 h-4" /> {selectedSuggestion ? "Fyll på" : "Lägg till"}
          </Button>
        </form>

        {/* Suggestions — inline list, pushes content down instead of covering it */}
        {showSuggestions && suggestions.length > 0 && !selectedSuggestion && (
          <div className="mt-2 bg-[#12161e] border border-white/[0.08] rounded-xl overflow-hidden" data-testid="inventory-suggestions">
            <p className="px-4 pt-2.5 pb-1 text-[10px] uppercase tracking-wider text-white/30">Finns redan i förrådet — tryck för att fylla på</p>
            {suggestions.map(s => (
              <button key={s.id} type="button"
                onClick={() => handleSelectSuggestion(s)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-sm hover:bg-[#6dbf4a]/10 active:bg-[#6dbf4a]/15 transition-colors text-left border-t border-white/[0.04]"
                data-testid={`suggestion-${s.id}`}>
                <span className="text-white font-medium truncate">{s.name}</span>
                <span className="text-[#6dbf4a] text-xs bg-[#6dbf4a]/10 px-2 py-0.5 rounded flex-shrink-0">{s.quantity} {s.unit}</span>
              </button>
            ))}
          </div>
        )}

        {/* Selected suggestion info */}
        {selectedSuggestion && (
          <p className="text-xs text-[#6dbf4a] mt-1.5">
            "{selectedSuggestion.name}" finns redan med {selectedSuggestion.quantity} {selectedSuggestion.unit} — antal du anger läggs till
          </p>
        )}
      </div>

      {/* Search */}
      {items.length > 5 && (
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Sök i inventarie..."
            className="bg-[#0d1218] border-white/[0.06] text-white pl-9"
            data-testid="search-inventory" />
        </div>
      )}

      {/* Items list */}
      {filteredItems.length > 0 ? (
        <div className="space-y-1">
          {filteredItems.map(item => (
            <div key={item.id}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-[#0d1218] hover:bg-[#111820] transition-colors group"
              data-testid={`inventory-item-${item.id}`}>

              {editingId === item.id ? (
                <div className="flex-1 flex items-center gap-2 flex-wrap">
                  <Input value={editForm.name} onChange={(e) => setEditForm(p => ({ ...p, name: e.target.value }))}
                    className="bg-[#080c12] border-white/[0.06] text-white flex-1 h-8 text-sm min-w-[120px]"
                    autoFocus onKeyDown={(e) => { if (e.key === 'Enter') handleEditSave(); if (e.key === 'Escape') setEditingId(null); }} />
                  <Input value={editForm.quantity} onChange={(e) => setEditForm(p => ({ ...p, quantity: e.target.value }))}
                    className="bg-[#080c12] border-white/[0.06] text-white w-20 h-8 text-sm text-center" type="number" step="any" />
                  <Input value={editForm.unit} onChange={(e) => setEditForm(p => ({ ...p, unit: e.target.value }))}
                    className="bg-[#080c12] border-white/[0.06] text-white w-14 h-8 text-sm text-center" />
                  <button onClick={handleEditSave} className="text-[#6dbf4a] hover:text-white"><Check className="w-4 h-4" /></button>
                  <button onClick={() => setEditingId(null)} className="text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <>
                  {/* Quick +/- buttons */}
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <button onClick={() => handleQuickAdjust(item, -1)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                      data-testid={`minus-${item.id}`}>
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className={cn(
                      "w-12 text-center font-bold text-sm",
                      item.quantity === 0 ? "text-red-400" : item.quantity <= 3 ? "text-amber-400" : "text-white"
                    )} data-testid={`qty-${item.id}`}>
                      {item.quantity}
                    </span>
                    <button onClick={() => handleQuickAdjust(item, 1)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-white/30 hover:text-[#6dbf4a] hover:bg-[#6dbf4a]/10 transition-colors"
                      data-testid={`plus-${item.id}`}>
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {/* Name + unit */}
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-white font-medium">{item.name}</span>
                    <span className="text-xs text-white/25 ml-1.5">{item.unit}</span>
                    {item.updated_by_name && (
                      <p className="text-[11px] text-white/20 mt-0.5">Uppdaterad av {item.updated_by_name}</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 sm:opacity-100 transition-opacity flex-shrink-0">
                    <button onClick={() => startEdit(item)}
                      className="w-9 h-9 flex items-center justify-center rounded-lg text-white/30 hover:text-white hover:bg-white/[0.06] transition-colors"
                      data-testid={`edit-${item.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(item.id)}
                      className="w-9 h-9 flex items-center justify-center rounded-lg text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                      data-testid={`delete-${item.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <Package className="w-10 h-10 mx-auto mb-3 text-gray-700" />
          <p className="text-white/40 text-sm">{searchQuery ? "Inga artiklar matchar sökningen" : "Tomt förråd"}</p>
          <p className="text-white/25 text-xs mt-1">Lägg till material ovan</p>
        </div>
      )}
    </div>
  );
};

export default Inventory;
